/**
 * Which cities hladom covers, drawn on the map.
 *
 * Someone opening this in Zagreb should see where it works, rather than
 * discovering the answer by getting a route rejected. The areas appear when
 * you are zoomed out far enough that they are useful and disappear once you
 * are close enough to be walking, where they would only be clutter.
 *
 * The list comes from the API rather than being hardcoded, so adding a city is
 * still one table in the Worker plus an upload — the map follows on its own.
 */

import type { Map as MlMap } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'
import { API_BASE } from './api-base'

const SRC = 'hladom-coverage'
const FILL = 'hladom-coverage-fill'
const LINE = 'hladom-coverage-line'
const LABEL = 'hladom-coverage-label'

/** Hidden past this zoom: by then you are looking at streets, not choosing a city. */
const MAX_ZOOM = 10.5

export interface City {
  id: string
  name: string
  /** [minLat, minLon, maxLat, maxLon] */
  bbox: [number, number, number, number]
}

export async function fetchCities(signal?: AbortSignal): Promise<City[]> {
  const res = await fetch(`${API_BASE}/api/health`, { signal })
  if (!res.ok) return []
  const body = (await res.json()) as { cities?: City[] }
  return body.cities ?? []
}

function toGeoJSON(cities: City[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: cities.map((c) => {
      const [minLat, minLon, maxLat, maxLon] = c.bbox
      return {
        type: 'Feature',
        properties: { name: c.name, id: c.id },
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [minLon, minLat],
            [maxLon, minLat],
            [maxLon, maxLat],
            [minLon, maxLat],
            [minLon, minLat],
          ]],
        },
      }
    }),
  }
}

/**
 * Frame every covered city at once.
 *
 * Used when someone asks for a route we cannot serve: telling them it does not
 * work here, while leaving them looking at the city where it does not work, is
 * only half an answer. Zooming out far enough also brings the coverage overlay
 * into view, since that is drawn below zoom 10.5.
 */
export function fitToCoverage(map: MlMap, cities: City[], bottomPadding = 0) {
  if (!cities.length) return
  const bounds = new maplibregl.LngLatBounds()
  for (const c of cities) {
    const [minLat, minLon, maxLat, maxLon] = c.bbox
    bounds.extend([minLon, minLat])
    bounds.extend([maxLon, maxLat])
  }
  map.fitBounds(bounds, {
    padding: { top: 60, left: 40, right: 40, bottom: bottomPadding + 40 },
    duration: 900,
    // Never closer than the zoom the coverage areas appear at, or the user
    // would be told where it works and then shown nothing.
    maxZoom: 9.5,
  })
}

export function addCoverage(map: MlMap, cities: City[]) {
  if (cities.length === 0) return
  const data = toGeoJSON(cities)

  if (map.getSource(SRC)) {
    ;(map.getSource(SRC) as maplibregl.GeoJSONSource).setData(data)
    return
  }

  map.addSource(SRC, { type: 'geojson', data })

  // Beneath the route, so a drawn route always reads on top.
  const before = map.getLayer('hladom-route-casing') ? 'hladom-route-casing' : undefined

  map.addLayer(
    {
      id: FILL,
      type: 'fill',
      source: SRC,
      maxzoom: MAX_ZOOM,
      paint: {
        'fill-color': '#06b6d4',
        // Fades out as you zoom in rather than vanishing abruptly.
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.18, MAX_ZOOM, 0.04],
      },
    },
    before,
  )

  map.addLayer(
    {
      id: LINE,
      type: 'line',
      source: SRC,
      maxzoom: MAX_ZOOM,
      paint: {
        'line-color': '#0e7490',
        'line-width': 1.5,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.9, MAX_ZOOM, 0.2],
      },
    },
    before,
  )

  map.addLayer(
    {
      id: LABEL,
      type: 'symbol',
      source: SRC,
      maxzoom: MAX_ZOOM,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 10, 15],
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#0e7490',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    },
    before,
  )

  // Tapping a covered area is the obvious thing to try, so make it work.
  map.on('click', FILL, (e) => {
    const f = e.features?.[0]
    if (!f) return
    const bbox = cities.find((c) => c.id === f.properties?.id)?.bbox
    if (!bbox) return
    const [minLat, minLon, maxLat, maxLon] = bbox
    map.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding: 40, duration: 900 },
    )
  })
  map.on('mouseenter', FILL, () => {
    map.getCanvas().style.cursor = 'pointer'
  })
  map.on('mouseleave', FILL, () => {
    map.getCanvas().style.cursor = ''
  })
}
